<template>
  <Toast />
  <div class="signup-container">
    <div class="sidebar-container d-none d-lg-block">
      <AuthSidebar />
      <Sidebar v-model:visible="visibleLeft" :baseZIndex="10000"> </Sidebar>
    </div>

    <div class="content-container">
      <div class="auth-card">
        <div class="logo-container">
          <img
            loading="lazy"
            src="/img/logo.svg"
            alt="CrowdHrs"
            title="CrowdHrs"
            class="form-logo"
          />
        </div>
        
        <div v-if="feedback" class="notification error">
          <span class="notification-text">{{ feedbackMassage }}</span>
          <button class="notification-close" @click="feedback = false">×</button>
        </div>

        <form @submit.prevent="handleLogin(!v$.$invalid)" class="form-fields">
          <!-- Name fields -->
          <div class="form-row">
            <div class="form-group">
              <span class="p-float-label">
                <InputText
                  id="firstname"
                  v-model="v$.firstname.$model"
                  :class="{ 'input-error': v$.firstname.$invalid && submitted }"
                  class="w-100"
                />
                <label for="firstname">{{ $t('firstname') }}</label>
              </span>
              <small v-if="v$.firstname.$invalid && submitted" class="error-text">
                {{ $t('firstname') }} is required
              </small>
            </div>

            <div class="form-group">
              <span class="p-float-label">
                <InputText
                  id="lastname"
                  v-model="v$.lastname.$model"
                  :class="{ 'input-error': v$.lastname.$invalid && submitted }"
                  class="w-100"
                />
                <label for="lastname">{{ $t('lastname') }}</label>
              </span>
              <small v-if="v$.lastname.$invalid && submitted" class="error-text">
                {{ $t('lastname') }} is required
              </small>
            </div>
          </div>

          <!-- Email and Password -->
          <div class="form-row">
            <div class="form-group">
              <span class="p-float-label">
                <InputText
                  id="email"
                  type="email"
                  v-model="v$.email.$model"
                  :class="{ 'input-error': v$.email.$invalid && submitted }"
                  class="w-100"
                />
                <label for="email">{{ $t('email') }}</label>
              </span>
              <small v-if="v$.email.$invalid && submitted" class="error-text">
                Please enter a valid email address
              </small>
            </div>

            <div class="form-group">
              <span class="p-float-label">
                <Password
                  id="password"
                  v-model="v$.password.$model"
                  :class="{ 'input-error': v$.password.$invalid && submitted }"
                  toggleMask
                  inputClass="w-100"
                >
                  <template #header>
                    <h6>{{ $t('pickpassowrd') }}</h6>
                  </template>
                  <template #footer="sp">
                    {{ sp.level }}
                    <Divider />
                    <p class="mt-2">{{ $t('sugges') }}</p>
                    <ul class="pl-2 ml-2 mt-0" style="line-height: 1.5">
                      <li>{{ $t('onelower') }}</li>
                      <li>{{ $t('oneuppercase') }}</li>
                      <li>{{ $t('onenumeric') }}</li>
                      <li>{{ $t('mineightchar') }}</li>
                    </ul>
                  </template>
                </Password>
                <label for="password">{{ $t('password') }}</label>
              </span>
              <small v-if="v$.password.$invalid && submitted" class="error-text">
                Password is required
              </small>
            </div>
          </div>

          <!-- LinkedIn and Company -->
          <div class="form-row">
            <div class="form-group">
              <span class="p-float-label">
                <InputText
                  id="linkdenPage"
                  v-model="v$.linkdenPage.$model"
                  :class="{ 'input-error': v$.linkdenPage.$invalid && submitted }"
                  class="w-100"
                />
                <label for="linkdenPage">{{ $t('linkedinPage') }}</label>
              </span>
              <small v-if="v$.linkdenPage.$invalid && submitted" class="error-text">
                {{ linkedinError }}
              </small>
            </div>

            <div class="form-group">
              <span class="p-float-label">
                <Dropdown
                  id="company"
                  v-model="v$.company.$model"
                  :options="companies"
                  optionLabel="description"
                  :class="{ 'input-error': v$.company.$invalid && submitted }"
                  class="w-100"
                />
                <label for="company">How did you hear about us?</label>
              </span>
              <small v-if="v$.company.$invalid && submitted" class="error-text">
                Please select an option
              </small>
            </div>
          </div>

          <!-- Reference (conditional) -->
          <div class="form-group" v-if="company.name == 'Referance'">
            <span class="p-float-label">
              <InputText
                id="referance"
                v-model="referance"
                class="w-100"
              />
              <label for="referance">{{ $t('referance') }}</label>
            </span>
          </div>

          <!-- Terms and conditions -->
          <div class="terms-container">
            <div class="checkbox-wrapper">
              <Checkbox
                inputId="accept"
                name="accept"
                value="Accept"
                v-model="v$.accept.$model"
                :class="{ 'input-error': v$.accept.$invalid && submitted }"
              />
              <label for="accept" class="m-0" :class="{ 'text-error': v$.accept.$invalid && submitted }">
                {{ $t('agreement') }}
              </label>
            </div>
            <router-link :to="{ name: 'termsOfService' }" class="terms-link">
              {{ $t('terms') }}
            </router-link>
          </div>

          <!-- Submit button -->
          <div class="form-actions">
            <CButton
              type="submit"
              :label="$t('signupm')"
              class="w-100"
              variant="solid"
              color="secondary"
              :loading="loading"
            />
          </div>

          <!-- Social signup -->
          <div class="social-login">
            <div class="social-divider">
              <span class="divider-line"></span>
              <span class="divider-text">Or sign up with social network</span>
              <span class="divider-line"></span>
            </div>

            <div class="social-buttons">
              <Button
                class="social-button linkedin-button"
                @click="loginWithLinkedin"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 48 48"
                >
                  <path
                    fill="#0288D1"
                    d="M42,37c0,2.762-2.238,5-5,5H11c-2.761,0-5-2.238-5-5V11c0-2.762,2.239-5,5-5h26c2.762,0,5,2.238,5,5V37z"
                  ></path>
                  <path
                    fill="#FFF"
                    d="M12 19H17V36H12zM14.485 17h-.028C12.965 17 12 15.888 12 14.499 12 13.08 12.995 12 14.514 12c1.521 0 2.458 1.08 2.486 2.499C17 15.887 16.035 17 14.485 17zM36 36h-5v-9.099c0-2.198-1.225-3.698-3.192-3.698-1.501 0-2.313 1.012-2.707 1.99C24.957 25.543 25 26.511 25 27v9h-5V19h5v2.616C25.721 20.5 26.85 19 29.738 19c3.578 0 6.261 2.25 6.261 7.274L36 36 36 36z"
                  ></path>
                </svg>
                <span>Sign Up With LinkedIn</span>
                <div class="loader-container" v-if="linkedinLoading">
                  <svg
                    class="spinner"
                    width="20"
                    height="20"
                    viewBox="0 0 66 66"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle
                      class="path"
                      fill="none"
                      stroke-width="6"
                      stroke-linecap="round"
                      cx="33"
                      cy="33"
                      r="30"
                    ></circle>
                  </svg>
                </div>
              </Button>

              <Button
                class="social-button google-button"
                @click.prevent="loginWithGoogle"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 18 18"
                  aria-hidden="true"
                >
                  <title>Google</title>
                  <g fill="none" fill-rule="evenodd">
                    <path
                      fill="#4285F4"
                      d="M17.64 9.2045c0-.6381-.0573-1.2518-.1636-1.8409H9v3.4814h4.8436c-.2086 1.125-.8427 2.0782-1.7959 2.7164v2.2581h2.9087c1.7018-1.5668 2.6836-3.874 2.6836-6.615z"
                    ></path>
                    <path
                      fill="#34A853"
                      d="M9 18c2.43 0 4.4673-.806 5.9564-2.1805l-2.9087-2.2581c-.8059.54-1.8368.859-3.0477.859-2.344 0-4.3282-1.5831-5.036-3.7104H.9574v2.3318C2.4382 15.9832 5.4818 18 9 18z"
                    ></path>
                    <path
                      fill="#FBBC05"
                      d="M3.964 10.71c-.18-.54-.2822-1.1168-.2822-1.71s.1023-1.17.2823-1.71V4.9582H.9573A8.9965 8.9965 0 0 0 0 9c0 1.4523.3477 2.8268.9573 4.0418L3.964 10.71z"
                    ></path>
                    <path
                      fill="#EA4335"
                      d="M9 3.5795c1.3214 0 2.5077.4541 3.4405 1.346l2.5813-2.5814C13.4632.8918 11.426 0 9 0 5.4818 0 2.4382 2.0168.9573 4.9582L3.964 7.29C4.6718 5.1627 6.6559 3.5795 9 3.5795z"
                    ></path>
                  </g>
                </svg>
                <span>Sign Up With Google</span>
                <div class="loader-container" v-if="googleLoading">
                  <svg
                    class="spinner"
                    width="20"
                    height="20"
                    viewBox="0 0 66 66"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle
                      class="path"
                      fill="none"
                      stroke-width="6"
                      stroke-linecap="round"
                      cx="33"
                      cy="33"
                      r="30"
                    ></circle>
                  </svg>
                </div>
              </Button>
            </div>
          </div>
        </form>

        <!-- Login link -->
        <div class="create-account">
          <span>{{ $t('hasaccount') }}</span>
          <router-link :to="{ name: 'login' }" class="login-link">
            {{ $t('loginm') }}
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { required, email, helpers } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'
import AuthSidebar from '../components/AuthSidebar.vue'
import { getItem } from '../store/localStorage'
import { post } from '../api/Services'
import { mapState } from 'vuex'
import { useLinkedIn, LinkedInCallback } from 'vue3-linkedin-login'
import { googleTokenLogin, decodeCredential, googleOneTap, googleLogout } from 'vue3-google-login'

export default {
  name: 'Signup',
  setup() {
    const v$ = useVuelidate()
    const { linkedInLogin, exchangeCodeForToken, getAccount, getMail } =
      useLinkedIn({
        clientId: process.env.VUE_APP_LINKEDIN_CLIENT_ID,
        clientSecret: process.env.VUE_APP_LINKEDIN_CLIENT_SECRET,
        redirectUri: process.env.VUE_APP_LINKEDIN_REDIRECT_URI,
        onSuccess: async (code) => {
          try {
            const exchangeCode = await exchangeCodeForToken(code)
            const account = await getAccount(exchangeCode.access_token)
            const email = await getMail(exchangeCode.access_token)

            if (!account || !email) {
              console.log('Account or email information not found.')
              return
            }

            const firstName = account.localizedFirstName
            const lastName = account.localizedLastName
            const emailAddress = email.elements[0]['handle~'].emailAddress

            const user = {
              firstname: firstName,
              lastname: lastName,
              email: emailAddress,
              password: null,
              linkedInPage: null,
              marketingChannelId: 7,
            }

            // Signup with LinkedIn data
            post('/Users/User-SignUp', user)
              .then((data) => {
                if (data[0].status == 0) {
                  this.account.emailConfirm = this.randomString(10)
                  sessionStorage.setItem(
                    'emailConfirm',
                    this.account.emailConfirm
                  )
                  this.$router.push({
                    name: 'login',
                    query: {
                      status: this.account.emailConfirm,
                      message: this.generateSeoUrl(data[0].message),
                      severity: 'success',
                    },
                  })
                }
              })
              .catch((error) => {
                this.feedback = true
                this.feedbackMassage = error
              })
          } catch (error) {
            console.log('Error:', error)
          }
        },
        scope: 'openid,profile,email',
        onError: (error) => {
          console.log(error)
        },
      })
    return {
      v$,
      linkedInLogin,
    }
  },
  components: {
    AuthSidebar,
  },

  validations() {
    return {
      firstname: {
        required,
      },

      lastname: {
        required,
      },
      email: {
        required,
        email,
      },
      password: {
        required,
      },

      accept: {
        required,
      },
      company: {
        required,
      },
      linkdenPage: {
        regex: helpers.regex(/^(https?:\/\/)?(www\.)?linkedin\.com\/in\/.+/),
        required,
      },

      visibleLeft: true,
    }
  },
  data() {
    return {
      errorBorderColor: '#B00020 !important',
      colorRed: { color: '#B00020' },
      loading: false,
      message: '',
      display: false,
      firstname: '',
      lastname: '',
      email: '',
      linkdenPage: '',
      company: '',
      googleLoading: false,
      password: '',
      submitted: false,
      identifyCodes: '234567890abcdefgxyzkmnAp',
      identifyCode: '',
      accept: '',
      securityCode: '',
      companies: [],
      googleUserInfo: {},
      user: {},
      roleId: getItem('roleId'),
      socail: false,
      feedback: false,
      feedbackMassage: '',
      linkedinLoading: false,
      clientId: process.env.VUE_APP_LINKEDIN_CLIENT_ID,
      clientSecret: process.env.VUE_APP_LINKEDIN_CLIENT_SECRET,
      redirectUri: process.env.VUE_APP_LINKEDIN_REDIRECT_URI,
    }
  },

  computed: {
    loggedIn() {
      return this.$store.state.auth.status.loggedIn
    },
    ...mapState({
      account: (state) => state.account,
    }),
    linkedinError() {
      if (this.v$.linkdenPage.required && this.v$.linkdenPage.$error) {
        return 'Please enter a valid LinkedIn profile URL'
      } else if (this.v$.linkdenPage.regex && this.v$.linkdenPage.$error) {
        return 'Please enter a valid LinkedIn profile URL*'
      } else {
        return ''
      }
    },
  },

  created() {
    this.identifyCode = ''
    this.makeCode(this.identifyCodes, 4)
    this.user.userId = -1
    this.user.sqlId = 22
    let parameters = [
      /*{ parameter_name: "pin_user_id", parameter_value: -1 }*/
    ]
    this.user.parameters = JSON.stringify(parameters)

    post('Commons/Executer', this.user).then((data) => {
      if (data) {
        this.companies = data
      }
    })
  },

  methods: {
    async handleGoogleUserInfo(response) {
      // Common method to handle Google user info from any Google auth method
      try {
        let userData = null;
        let token = null;
        
        if (response.credential) {
          // Handle credential from One Tap
          userData = decodeCredential(response.credential);
          this.googleUserInfo = {
            firstname: userData.given_name || '',
            lastname: userData.family_name || '',
            email: userData.email || '',
            password: null,
            linkedInPage: null,
            marketingChannelId: 7,
            googleAuthId: userData.sub || '',
          };
        } else if (response.access_token) {
          // Handle access token from googleTokenLogin
          token = response.access_token;
          
          // Use the token to get user information from Google API
          const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          
          if (userInfoResponse.ok) {
            userData = await userInfoResponse.json();
            
            this.googleUserInfo = {
              firstname: userData.given_name || '',
              lastname: userData.family_name || '',
              email: userData.email || '',
              password: null,
              linkedInPage: null,
              marketingChannelId: 7,
              googleAuthId: userData.sub || '',
              access_token: token
            };
          }
        }
        
        if (this.googleUserInfo && this.googleUserInfo.email) {
          const googleSignup = await this.$store.dispatch(
            'auth/googleAccount',
            this.googleUserInfo
          );
          
          if (googleSignup) {
            this.$router.push({
              name: 'jobOpenings',
            });
          }
        }
      } catch (error) {
        console.error('Error processing Google user info:', error);
        this.feedback = true;
        this.feedbackMassage = error.toString();
      }
    },

    async loginWithGoogle() {
      this.googleLoading = true;
      try {
        const response = await googleTokenLogin();
        
        if (response) {
          await this.handleGoogleUserInfo(response);
        }
      } catch (error) {
        // Silently handle popup closed errors
        if (error.error !== 'popup_closed_by_user' && 
            error.message !== 'Popup window closed' && 
            !error.toString().includes('Popup window closed')) {
          this.$toast.add({
            severity: 'error',
            summary: 'Error',
            detail: error,
            life: 3000,
          });
        } else {
          console.log('User closed the Google login popup');
        }
      } finally {
        this.googleLoading = false;
      }
    },

    generateSeoUrl(text) {
      return text
        .toString() // Ensure the input is a string
        .normalize('NFD') // Normalize to decompose combined letters (e.g., é -> e + ´)
        .replace(/[\u0300-\u036f]/g, '') // Remove diacritics (accents, etc.)
        .toLowerCase() // Convert to lower case
        .trim() // Remove leading and trailing whitespace
        .replace(/[^a-z0-9\s-]/g, '') // Remove all non-alphanumeric characters except spaces and hyphens
        .replace(/\s+/g, '-') // Replace spaces with hyphens
        .replace(/-+/g, '-') // Replace multiple hyphens with a single hyphen
    },

    logIn() {
      this.$router.push({ name: 'login' })
    },
    isRequired(value) {
      if (value && value.trim()) {
        return true
      }

      return 'This is required'
    },
    goDashboard() {
      window.location = '/'
    },

    handleLogin(isFormValid) {
      this.feedback = false
      this.message = ''
      this.submitted = true

      if (!isFormValid) {
        return
      }
      this.loading = true
      this.user.firstname = this.firstname
      this.user.lastname = this.lastname
      this.user.email = this.email
      this.user.password = this.password
      this.user.linkedInPage = this.linkdenPage
      this.user.marketingChannelId = this.company.id

      post('/Users/User-SignUp', this.user)
        .then((data) => {
          if (data[0].status == 0) {
            this.account.emailConfirm = this.randomString(10)
            sessionStorage.setItem('emailConfirm', this.account.emailConfirm)
            console.log({
              params: {
                status: this.account.emailConfirm,
                message: this.generateSeoUrl(data[0].message),
                severity: 'success',
              },
            })

            this.$router.push({
              name: 'login',
              query: {
                status: this.account.emailConfirm,
                message: this.generateSeoUrl(data[0].message),
                severity: 'success',
              },
            })
          } else {
            this.feedback = true
            this.feedbackMassage = data[0].message
            this.loading = false
          }
        })
        .catch((error) => {
          this.feedback = true
          this.feedbackMassage = error
        })
    },
    randomString(length) {
      let result = ''
      const characters =
        'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
      const charactersLength = characters.length
      for (let i = 0; i < length; i++) {
        result += characters.charAt(
          Math.floor(Math.random() * charactersLength)
        )
      }
      return result
    },
    onChange(v) {},
    onComplete(v) {},
    randomNum(min, max) {
      return Math.floor(Math.random() * (max - min) + min)
    },

    makeCode(o, l) {
      for (let i = 0; i < l; i++) {
        this.identifyCode +=
          this.identifyCodes[this.randomNum(0, this.identifyCodes.length)]
      }
    },
    async loginWithLinkedin() {
      this.linkedinLoading = true
      try {
        const scopes = ['openid', 'profile', 'email']
        const state = this.generateState()
        window.sessionStorage.setItem('linkedin_redirect', '/chrs/job-openings')
        window.sessionStorage.setItem('linkedin_state', state)
        window.sessionStorage.setItem('linkedin_signup', 'true') // Signup için flag

        const linkedInAuthUrl = `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${
          this.clientId
        }&redirect_uri=${encodeURIComponent(
          this.redirectUri
        )}&scope=${encodeURIComponent(scopes.join(' '))}&state=${state}`

        window.location.href = linkedInAuthUrl
      } catch (error) {
        this.feedback = true
        this.feedbackMassage = error.toString()
      } finally {
        this.linkedinLoading = false
      }
    },

    generateState() {
      return Math.random().toString(36).substring(2, 15)
    },
  },
  mounted() {
    LinkedInCallback();
    
    // Initialize Google One Tap with auto login for signup flow
    googleOneTap({ 
      autoLogin: false,
      cancelOnTapOutside: false, // Prevent dismissal by tapping outside
      context: 'signup', // Use 'signup' context for the One Tap prompt
      callback: (response) => {
        console.log('Google One Tap callback response:', response);
        this.handleGoogleUserInfo(response);
      },
      onError: (error) => {
        // Only log popup closed errors, don't show them to the user
        if (error.type !== 'popup_closed_by_user' && 
            error.message !== 'Popup window closed' && 
            !error.toString().includes('Popup window closed')) {
          console.error('Google One Tap error:', error);
        } else {
          console.log('User closed the Google One Tap dialog');
        }
      }
    })
      .then(async (response) => {
        console.log('Google One Tap promise resolved:', response);
        if (response) {
          await this.handleGoogleUserInfo(response);
        }
      })
      .catch((error) => {
        // Silently handle popup closed errors
        if (error.error !== 'popup_closed_by_user' && 
            error.message !== 'Popup window closed' && 
            !error.toString().includes('Popup window closed')) {
          console.error('Google One Tap promise rejected:', error);
          console.error('Google One Tap error details:', error);
        } else {
          console.log('User dismissed the Google One Tap dialog');
        }
      });
  },
}
</script>

<style lang="scss" scoped>
.signup-container {
  display: flex;
  min-height: 100vh;
  width: 100%;
  background-color: #f7f9fc;
}

.sidebar-container {
  flex: 0 0 33.333%;
  height: 100vh;
}

.content-container {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1.5rem;
}

.auth-card {
  width: 100%;
  max-width: 550px;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  padding: 2rem;
  margin: 0 auto;
  transition: all 0.2s ease;
}

.logo-container {
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
}

.form-logo {
  max-width: 180px;
  height: auto;
}

.notification {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  border-radius: 6px;
  margin-bottom: 1.5rem;
  
  &.error {
    background-color: rgba(220, 53, 69, 0.1);
    border-left: 3px solid #dc3545;
    color: #dc3545;
  }
  
  .notification-close {
    background: transparent;
    border: none;
    font-size: 1.25rem;
    cursor: pointer;
    color: inherit;
  }
}

.form-fields {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-row {
  display: flex;
  gap: 1.5rem;
  
  .form-group {
    flex: 1;
  }
}

.form-group {
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  margin-bottom: 0 !important;
  
  ::v-deep(.p-float-label) {
    display: block;
    width: 100%;
    margin-bottom: 0.25rem;
    
    label {
      color: #6c757d;
    }
    
    input:focus ~ label,
    .p-inputwrapper-focus ~ label {
      color: #015cac;
    }
  }
  
  ::v-deep(:not(.p-dropdown) .p-inputtext),
  ::v-deep(.p-password input) {
    width: 100% !important;
    padding: 0.75rem;
    font-size: 1rem;
    background-color: #fff;
    border: 1px solid #ced4da;
    border-radius: 6px;
    transition: all 0.25s;
    box-sizing: border-box;
    
    &:hover {
      border-color: #015cac;
    }
    
    &:focus {
      border-color: #015cac;
      box-shadow: 0 0 0 2px rgba(1, 92, 172, 0.2);
    }
    
    &.input-error {
      border-color: #dc3545;
      
      &:focus {
        box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.25);
      }
    }
  }

  ::v-deep(.p-dropdown) {
    width: 100% !important;
    border: 1px solid #ced4da;
    border-radius: 6px;
    background-color: #fff;
    transition: all 0.25s;
    
    &:hover {
      border-color: #015cac;
    }
    
    &:focus, &.p-focus {
      border-color: #015cac;
      box-shadow: 0 0 0 2px rgba(1, 92, 172, 0.2);
    }
    
    &.input-error {
      border-color: #dc3545;
    }
    
    .p-dropdown-label {
      padding: 0.75rem;
      display: flex;
      align-items: center;
    }
  }
  
  .error-text {
    color: #dc3545;
    margin-top: 0.25rem;
    font-size: 0.8rem;
  }
}

.terms-container {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  margin: 0.5rem 0;
  
  .checkbox-wrapper {
    display: flex;
    align-items: center;
    
    ::v-deep(.p-checkbox) {
      margin-right: 0.5rem;
    }
  }
  
  label {
    color: #6c757d;
    
    &.text-error {
      color: #dc3545;
    }
  }
  
  .terms-link {
    color: #015cac;
    text-decoration: none;
    margin-left: 0.5rem;
    
    &:hover {
      text-decoration: underline;
    }
  }
}

.form-actions {
  margin-top: 0.5rem;
  
  ::v-deep(.chrs-btn-secondary) {
    height: 48px;
    border-radius: 6px;
    font-weight: 500;
    transition: all 0.25s ease;
  }
}

.social-login {
  margin-top: 2rem;
  
  .social-divider {
    display: flex;
    align-items: center;
    margin-bottom: 1.5rem;
    
    .divider-line {
      flex-grow: 1;
      height: 1px;
      background-color: #dee2e6;
    }
    
    .divider-text {
      padding: 0 1rem;
      color: #6c757d;
      font-size: 0.9rem;
    }
  }
  
  .social-buttons {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    
    .social-button {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.75rem;
      height: 48px;
      border-radius: 6px;
      font-weight: 500;
      transition: all 0.25s ease;
      position: relative;
      border: 1px solid #dee2e6;
      background-color: #fff;
      color: #333;
      
      &:hover {
        background-color: #f8f9fa;
      }
      
      .loader-container {
        position: absolute;
        right: 1rem;
      }
      
      span {
        flex-grow: 1;
        text-align: center;
        padding-right: 2rem;
      }
    }
    
    .linkedin-button {
      &:hover {
        background-color: rgba(0, 119, 181, 0.1);
        border-color: #0077b5;
      }
    }
    
    .google-button {
      &:hover {
        background-color: rgba(219, 68, 55, 0.1);
        border-color: #db4437;
      }
    }
  }
}

.create-account {
  margin-top: 2rem;
  text-align: center;
  
  span {
    color: #6c757d;
    font-size: 0.95rem;
  }
  
  .login-link {
    margin-left: 0.5rem;
    color: #015cac;
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
}

.spinner {
  animation: rotator 1.4s linear infinite;
}

@keyframes rotator {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(270deg);
  }
}

.path {
  stroke-dasharray: 187;
  stroke-dashoffset: 0;
  transform-origin: center;
  animation: dash 1.4s ease-in-out infinite;
  stroke: #015cac;
}

@keyframes dash {
  0% {
    stroke-dashoffset: 187;
  }
  50% {
    stroke-dashoffset: 46.75;
    transform: rotate(135deg);
  }
  100% {
    stroke-dashoffset: 187;
    transform: rotate(450deg);
  }
}

::v-deep(.p-password) {
  width: 100%;
  
  .p-password-panel {
    margin-top: 0.5rem;
  }
}

@media (max-width: 991.98px) {
  .signup-container {
    flex-direction: column;
  }
  
  .content-container {
    min-height: 100vh;
    padding: 2rem 1rem;
  }
  
  .form-row {
    flex-direction: column;
    gap: 1.5rem;
  }
}
</style>
